import { NavLink, useNavigate, Outlet, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/constants";
import {
  LayoutDashboard, UserPlus, GitBranch, Users, Building2, Wallet,
  BarChart3, MessageCircle, Shield, LogOut, Menu, X, Package, Mail, KeyRound, Contact, DatabaseBackup, Bell, Megaphone, Facebook, ReceiptText, ListChecks, History, Globe, Settings, FileText, ScrollText, Layers, ChevronDown, Landmark, Gift, ClipboardCheck,
} from "lucide-react";
import { useState } from "react";
import { LogoMark } from "@/components/Logo";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, page: "dashboard", end: true },
  { to: "/dashboard/leads", label: "Leads", icon: UserPlus, page: "leads" },
  { to: "/dashboard/pipeline", label: "Loan Pipeline", icon: GitBranch, page: "pipeline" },
  { to: "/dashboard/ops-approval", label: "Ops Approval", icon: ListChecks, page: "ops-approval" },
  { to: "/dashboard/partners", label: "Partners", icon: Users, page: "partners" },
  { to: "/dashboard/lenders", label: "Lenders", icon: Building2, page: "lenders" },
  { to: "/dashboard/products", label: "Products", icon: Package, page: "products" },
  { to: "/dashboard/commissions", label: "Commissions", icon: Wallet, page: "commissions" },
  { to: "/dashboard/finance", label: "Finance", icon: ReceiptText, page: "finance" },
  { to: "/dashboard/reports", label: "Reports", icon: BarChart3, page: "reports" },
  { to: "/dashboard/offers", label: "Offers", icon: Gift, page: "offers-view", public: true },
];

const SETTINGS_NAV = [
  { to: "/dashboard/notifications", label: "WhatsApp", icon: MessageCircle, page: "notifications" },
  { to: "/dashboard/users", label: "Team", icon: Shield, page: "users" },
  { to: "/dashboard/roles", label: "Roles", icon: KeyRound, page: "roles" },
  { to: "/dashboard/partner-types", label: "Partner Types", icon: Contact, page: "partner-types" },
  { to: "/dashboard/email-settings", label: "Email", icon: Mail, page: "email-settings" },
  { to: "/dashboard/email-notifications", label: "Email Rules", icon: Bell, page: "email-notifications" },
  { to: "/dashboard/marketing", label: "Marketing", icon: Megaphone, page: "marketing" },
  { to: "/dashboard/meta-settings", label: "Meta Integration", icon: Facebook, page: "meta-settings" },
  { to: "/dashboard/website", label: "Website Content", icon: Globe, page: "website" },
  { to: "/dashboard/backup", label: "Data & Backup", icon: DatabaseBackup, page: "data-admin" },
  { to: "/dashboard/audit-logs", label: "Audit Log", icon: History, page: "audit-logs" },
  { to: "/dashboard/bill-items", label: "Bill Items", icon: ListChecks, page: "bill-items" },
  { to: "/dashboard/master-documents", label: "Master Documents", icon: FileText, page: "master-documents" },
  { to: "/dashboard/proposal-format", label: "Proposal Format", icon: ScrollText, page: "proposal-format" },
  { to: "/dashboard/bill-format", label: "Bill Format", icon: ReceiptText, page: "bill-format" },
  { to: "/dashboard/role-hierarchy", label: "Role Hierarchy", icon: Layers, page: "role-hierarchy" },
  { to: "/dashboard/field-rules", label: "Field Rules", icon: ListChecks, page: "field-rules" },
  { to: "/dashboard/agreement-format", label: "Agreement Format", icon: ScrollText, page: "agreement-format" },
  { to: "/dashboard/leegality", label: "Leegality e-Sign", icon: FileText, page: "leegality" },
  { to: "/dashboard/pdd", label: "PDD Checklist", icon: ListChecks, page: "pdd" },
  { to: "/dashboard/ops-check", label: "Ops Check Setup", icon: ListChecks, page: "ops-check" },
  { to: "/dashboard/company-banks", label: "Arjun Fintech Banks", icon: Landmark, page: "company-banks" },
  { to: "/dashboard/undertaking-format", label: "Undertaking Format", icon: ScrollText, page: "undertaking-format" },
  { to: "/dashboard/final-pdd", label: "Final PDD Update", icon: ClipboardCheck, page: "final-pdd" },
  { to: "/dashboard/login-fields", label: "Login Done Inputs", icon: ListChecks, page: "login-fields" },
  { to: "/dashboard/ftp-config", label: "FTP Server", icon: DatabaseBackup, page: "ftp-config" },
  { to: "/dashboard/offers-admin", label: "Offers", icon: Gift, page: "offers" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const perms = user?.permissions || [];
  const items = NAV.filter((n) => n.public || user?.role === "admin" || perms.includes(n.page));
  const settingsItems = SETTINGS_NAV.filter((n) => user?.role === "admin" || perms.includes(n.page));
  const bottomItems = items.slice(0, 4);

  if (user?.role === "partner") return <Navigate to="/portal" replace />;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const SidebarContent = () => (
    <>
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="px-6 py-5 flex items-center gap-3 border-b border-slate-800">
          <LogoMark className="w-9 h-9" />
          <div>
            <div className="text-white font-bold font-heading leading-tight">Arjun Fintech</div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-mono">DSA Platform</div>
          </div>
        </div>
        <nav className="px-3 py-4 space-y-1 flex-1 overflow-y-auto">
          {items.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setMobileOpen(false)}
              data-testid={`nav-${n.label.replace(/\s+/g, "-").toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  isActive ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/20"
                    : "text-slate-400 hover:text-white hover:bg-slate-800/60 border border-transparent"
                }`}>
              <n.icon size={18} />
              {n.label}
            </NavLink>
          ))}

          {settingsItems.length > 0 && (
            <div className="pt-1">
              <button type="button" data-testid="nav-settings-group" onClick={() => setSettingsOpen((o) => !o)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-800/60 border border-transparent transition-all">
                <Settings size={18} />
                <span className="flex-1 text-left">Settings</span>
                <ChevronDown size={16} className={`transition-transform ${settingsOpen ? "rotate-180" : ""}`} />
              </button>
              {settingsOpen && (
                <div className="mt-1 ml-3 pl-3 border-l border-slate-800 space-y-1">
                  {settingsItems.map((n) => (
                    <NavLink key={n.to} to={n.to} onClick={() => setMobileOpen(false)}
                      data-testid={`nav-${n.label.replace(/\s+/g, "-").toLowerCase()}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          isActive ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/20"
                            : "text-slate-400 hover:text-white hover:bg-slate-800/60 border border-transparent"
                        }`}>
                      <n.icon size={16} />
                      {n.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>
      </div>
      <div className="px-3 py-4 border-t border-slate-800">
        <div className="px-3 py-2">
          <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
          <div className="text-xs text-emerald-400 font-mono">{ROLE_LABELS[user?.role] || user?.role}</div>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="hidden lg:flex w-64 bg-slate-900 flex-col justify-between fixed inset-y-0 left-0 z-40">
        <SidebarContent />
      </aside>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-slate-900/60" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 bg-slate-900 flex flex-col justify-between">
            <button onClick={() => setMobileOpen(false)} className="absolute top-4 right-4 text-slate-400"><X size={20} /></button>
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="sticky top-0 z-30 h-16 bg-white/80 backdrop-blur-md border-b border-slate-200 px-4 sm:px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-2 -ml-2 text-slate-600" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button">
              <Menu size={22} />
            </button>
            <div className="text-sm text-slate-500 font-mono hidden sm:block">Direct Selling Agent Operations</div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-semibold text-slate-800 leading-tight" data-testid="header-user-name">{user?.name}</div>
              <div className="text-[11px] text-emerald-600 font-mono">{ROLE_LABELS[user?.role] || user?.role}</div>
            </div>
            <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-semibold text-sm">
              {user?.name?.charAt(0)?.toUpperCase()}
            </div>
            <button onClick={handleLogout} data-testid="header-logout-button"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 hover:text-rose-600 hover:bg-rose-50 border border-slate-200 transition-all">
              <LogOut size={16} /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto pb-24 lg:pb-8">
          <Outlet />
        </main>
      </div>

      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-slate-900 border-t border-slate-800 flex items-stretch justify-around pb-[env(safe-area-inset-bottom)]" data-testid="mobile-bottom-nav">
        {bottomItems.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end}
            data-testid={`bottomnav-${n.label.replace(/\s+/g, "-").toLowerCase()}`}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium transition-colors ${
                isActive ? "text-emerald-400" : "text-slate-400"
              }`}>
            <n.icon size={20} />
            <span className="truncate max-w-full px-1">{n.label}</span>
          </NavLink>
        ))}
        <button onClick={() => setMobileOpen(true)} data-testid="bottomnav-more"
          className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium text-slate-400">
          <Menu size={20} />
          <span>More</span>
        </button>
      </nav>
    </div>
  );
}
